/**
 * useTellerPicks — the per-room "who's talking" state for a group room (Step 11b, D3, D4).
 *
 * Owns three things the pane and the composer share:
 *   - the circle state per twin, derived from the saved `activeParticipants` and the picks;
 *   - the teller picks for the next send (persona tokens), from a circle tap or an @tag;
 *   - the Leads value shown by the selector (stored `tellerCount`, never above the twins in
 *     the round; with picks, the pick count, disabled: a tagged send tells with its picks).
 *
 * Off <-> In saves `activeParticipants` through `onSettingsChange` (optimistic in
 * useChatRoom). Leads next is local: it rides the next send as `tellerPicks`, then sits in a
 * "sent, pending" set (ring still shown, never handed to a later send) until that send's
 * outcome arrives in `sendOutcomes`: accepted clears it, failed (removed, trial cap) gives it
 * back, an edit moves it to the item's new id. A blocked item keeps it pending for its retry.
 *
 * Two gates: `canPickTellers` lets a viewer cycle circles at all (anyone who can send, like
 * an @tag); `canUpdateSettings` lets the cycle save (owner only). Without it a tap never
 * saves: Sitting out stays put, In -> Leads next on a ready twin is a local pick, Leads next
 * drops the pick back to the twin's saved state, and an In twin that is not ready stays In.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ChatRoomParticipantDto,
  ChatRoomSettings,
  DEFAULT_TELLER_COUNT,
} from '@digital-twins/shared-types'

export type TwinCircleState = 'off' | 'bidder' | 'teller'

/** Where a pick came from: a circle tap, a live @tag in the composer, or one a failed send gave back. */
type PickSource = 'circle' | 'tag' | 'held'

export interface TellerCountView {
  value: number
  min: number
  max: number
  isDisabled: boolean
}

export interface MentionUnlinkRequest {
  /** Participant id (the persona token), the mention id the composer tracks. */
  participantId: string
  nonce: number
}

export interface TellerPicksController {
  /** Circle state per persona token. */
  states: Record<string, TwinCircleState>
  /** Persona tokens picked to lead the next send, room order. */
  picks: Array<string>
  /** Twins in the next round (saved active set plus tag-picked twins). */
  bidderCount: number
  isAllOff: boolean
  canUpdateSettings: boolean
  /** The viewer may cycle circles for a local Leads next pick (saves still need canUpdateSettings). */
  canPickTellers: boolean
  tellerCount: TellerCountView
  cycle: (personaId: string) => void
  setTellerCount: (value: number) => void
  /** Composer: ids (participant ids, equal to persona tokens) of the @mentions linked in the text. */
  onMentionsChange: (participantIds: Array<string>) => void
  /** Composer: a mention the pane asked to unlink (text kept). */
  mentionUnlinkRequest: MentionUnlinkRequest | null
  /** Called when a message is queued; returns the picks it carries and marks them sent, pending. */
  takeSendPicks: () => Array<string>
  /** Binds the picks just taken to the queued send, so its outcome clears or restores exactly those. */
  markSent: (clientRequestId: string | undefined, picks: Array<string>) => void
}

interface UseTellerPicksArgs {
  /** The room the picks belong to; a new token starts from no picks (ChatPage stays mounted across rooms). */
  roomToken?: string | null
  participants: Array<ChatRoomParticipantDto>
  settings: ChatRoomSettings | undefined
  /** Saves (Off <-> In, tellerCount). Owner only. */
  canUpdateSettings: boolean
  /** Local Leads next picks from a circle tap; anyone who can send. Defaults to canUpdateSettings. */
  canPickTellers?: boolean
  onSettingsChange?: (partial: Partial<ChatRoomSettings>) => void | Promise<void>
  /** Send outcomes in arrival order (useChatRoom); accepted clears a send's picks, failed restores them. */
  sendOutcomes?: ReadonlyArray<SendOutcome>
}

/** Saved active set, filtered to current participants, room order. Missing key = everyone. */
export function resolveActivePersonaIds(
  participants: Array<ChatRoomParticipantDto>,
  settings: ChatRoomSettings | undefined,
): Array<string> {
  const saved = settings?.activeParticipants
  if (!saved) {
    return participants.map((p) => p.personaId)
  }
  const savedSet = new Set(saved)
  return participants.filter((p) => savedSet.has(p.personaId)).map((p) => p.personaId)
}

/** The state a tap moves a circle to. A twin that is not ready skips Leads next. */
export function nextCircleState(state: TwinCircleState, canLead: boolean): TwinCircleState {
  if (state === 'off') {
    return 'bidder'
  }
  if (state === 'bidder') {
    return canLead ? 'teller' : 'off'
  }
  return 'off'
}

/**
 * Leads selector. Untagged: min 1, max = twins in the round, the stored count clamped. Tagged
 * (picks > 0): the picks tell and the stepper is ignored (owner 2026-10-14), so it shows the pick
 * count, disabled.
 */
export function resolveTellerCountView(
  storedTellerCount: number | undefined,
  pickCount: number,
  bidderCount: number,
  canUpdateSettings: boolean,
): TellerCountView {
  const stored = Number.isInteger(storedTellerCount)
    ? (storedTellerCount as number)
    : DEFAULT_TELLER_COUNT
  if (pickCount > 0) {
    const value = Math.max(1, bidderCount > 0 ? Math.min(pickCount, bidderCount) : pickCount)
    return { value, min: value, max: value, isDisabled: true }
  }
  const min = 1
  const max = Math.max(min, bidderCount)
  if (bidderCount === 0) {
    return { value: stored, min, max, isDisabled: true }
  }
  const value = Math.min(max, Math.max(min, stored))
  return { value, min, max, isDisabled: !canUpdateSettings || min >= max }
}

function orderByRoom(participants: Array<ChatRoomParticipantDto>, ids: Set<string>): Array<string> {
  return participants.filter((p) => ids.has(p.personaId)).map((p) => p.personaId)
}

/** One send's outcome, in arrival order (useChatRoom `sendOutcomes`). */
export interface SendOutcome {
  clientRequestId: string
  /** rekeyed: an edited queued item now sends as `clientRequestId` (was `previousClientRequestId`). */
  outcome: 'accepted' | 'failed' | 'rekeyed'
  previousClientRequestId?: string
}

interface PicksState {
  /** Takeable picks for the next send. */
  sources: Record<string, PickSource>
  /** Sent, pending: persona token -> the clientRequestId (or take key) of the send carrying it. */
  pending: Record<string, string>
  /** Picks a failed send gave back: persona token -> that send's clientRequestId. */
  restoredFrom: Record<string, string>
}

const EMPTY_PICKS: PicksState = { sources: {}, pending: {}, restoredFrom: {} }

function withoutKeys<T>(record: Record<string, T>, keys: Iterable<string>): Record<string, T> {
  const next = { ...record }
  for (const key of keys) {
    delete next[key]
  }
  return next
}

/**
 * Applies one send outcome to the picks: accepted clears its picks, failed (the item left the
 * queue unsent) gives them back, rekeyed moves them to the edited item's new id. A blocked item
 * sends no outcome, so its picks stay pending and no later send can take them.
 */
export function applySendOutcome(state: PicksState, outcome: SendOutcome): PicksState {
  if (outcome.outcome === 'rekeyed') {
    const previous = outcome.previousClientRequestId
    const moved = Object.keys(state.pending).filter((personaId) => state.pending[personaId] === previous)
    if (!previous || moved.length === 0) {
      return state
    }
    const pending = { ...state.pending }
    for (const personaId of moved) {
      pending[personaId] = outcome.clientRequestId
    }
    return { ...state, pending }
  }
  const owned = Object.keys(state.pending).filter(
    (personaId) => state.pending[personaId] === outcome.clientRequestId,
  )
  if (outcome.outcome === 'accepted') {
    const restored = Object.keys(state.restoredFrom).filter(
      (personaId) =>
        state.restoredFrom[personaId] === outcome.clientRequestId && state.sources[personaId] === 'held',
    )
    if (owned.length === 0 && restored.length === 0) {
      return state
    }
    return {
      sources: withoutKeys(state.sources, restored),
      pending: withoutKeys(state.pending, owned),
      restoredFrom: withoutKeys(state.restoredFrom, restored),
    }
  }
  if (owned.length === 0) {
    return state
  }
  const sources = { ...state.sources }
  const restoredFrom = { ...state.restoredFrom }
  for (const personaId of owned) {
    if (!sources[personaId]) {
      sources[personaId] = 'held'
      restoredFrom[personaId] = outcome.clientRequestId
    }
  }
  return { sources, pending: withoutKeys(state.pending, owned), restoredFrom }
}

export function useTellerPicks(args: UseTellerPicksArgs): TellerPicksController {
  const {
    roomToken = null,
    participants,
    settings,
    canUpdateSettings,
    canPickTellers = canUpdateSettings,
    onSettingsChange,
    sendOutcomes,
  } = args

  // State stamped with another room's token reads as empty and is replaced on the next
  // write, so a pick held in room A never renders in room B, not even for one frame.
  const [stored, setStored] = useState<{ roomToken: string | null; picks: PicksState }>({
    roomToken,
    picks: EMPTY_PICKS,
  })
  const storedRef = useRef(stored)
  storedRef.current = stored
  const [unlinkState, setUnlinkState] = useState<{
    roomToken: string | null
    request: MentionUnlinkRequest | null
  }>({ roomToken, request: null })
  const unlinkNonceRef = useRef(0)
  const takeNonceRef = useRef(0)
  const lastTakeKeyRef = useRef<{ roomToken: string | null; key: string } | null>(null)
  const processedOutcomesRef = useRef<{ roomToken: string | null; count: number }>({
    roomToken,
    count: 0,
  })

  const picksState = stored.roomToken === roomToken ? stored.picks : EMPTY_PICKS
  const pickSources = picksState.sources
  const pendingPicks = picksState.pending
  const mentionUnlinkRequest = unlinkState.roomToken === roomToken ? unlinkState.request : null

  /** Every write goes through here so takeSendPicks sees the latest state synchronously. */
  const updatePicks = useCallback(
    (fn: (prev: PicksState) => PicksState) => {
      const current = storedRef.current
      const isSameRoom = current.roomToken === roomToken
      const prev = isSameRoom ? current.picks : EMPTY_PICKS
      const next = fn(prev)
      if (next === prev && isSameRoom) {
        return
      }
      const nextStored = { roomToken, picks: next }
      storedRef.current = nextStored
      setStored(nextStored)
    },
    [roomToken],
  )

  const setMentionUnlinkRequest = useCallback(
    (request: MentionUnlinkRequest) => setUnlinkState({ roomToken, request }),
    [roomToken],
  )

  const readPicks = useCallback(
    () => (storedRef.current.roomToken === roomToken ? storedRef.current.picks : EMPTY_PICKS),
    [roomToken],
  )

  const activePersonaIds = useMemo(
    () => resolveActivePersonaIds(participants, settings),
    [participants, settings],
  )

  const picks = useMemo(
    () => orderByRoom(participants, new Set(Object.keys(pickSources))),
    [participants, pickSources],
  )

  const states = useMemo(() => {
    const active = new Set(activePersonaIds)
    const out: Record<string, TwinCircleState> = {}
    for (const p of participants) {
      // A sent pick keeps its ring until Nest accepts that send.
      out[p.personaId] = pickSources[p.personaId] || pendingPicks[p.personaId]
        ? 'teller'
        : active.has(p.personaId)
          ? 'bidder'
          : 'off'
    }
    return out
  }, [participants, activePersonaIds, pickSources, pendingPicks])

  const bidderCount = useMemo(
    () => new Set([...activePersonaIds, ...picks, ...Object.keys(pendingPicks)].filter(
      (personaId) => participants.some((p) => p.personaId === personaId),
    )).size,
    [activePersonaIds, picks, pendingPicks, participants],
  )

  const tellerCount = useMemo(
    () => resolveTellerCountView(settings?.tellerCount, picks.length, bidderCount, canUpdateSettings),
    [settings?.tellerCount, picks.length, bidderCount, canUpdateSettings],
  )

  // Accepted sends clear exactly the picks they carried; failed ones give them back.
  useEffect(() => {
    const outcomes = sendOutcomes ?? []
    // A new room or a shorter list is a fresh one (useChatRoom resets it on a room change).
    const processed = processedOutcomesRef.current
    const start = processed.roomToken === roomToken && processed.count <= outcomes.length
      ? processed.count
      : 0
    processedOutcomesRef.current = { roomToken, count: outcomes.length }
    const fresh = outcomes.slice(start)
    if (fresh.length === 0) {
      return
    }
    updatePicks((prev) => fresh.reduce(applySendOutcome, prev))
  }, [sendOutcomes, roomToken, updatePicks])

  const saveActive = useCallback(
    (ids: Set<string>) => {
      void onSettingsChange?.({ activeParticipants: orderByRoom(participants, ids) })
    },
    [onSettingsChange, participants],
  )

  const cycle = useCallback(
    (personaId: string) => {
      if (!canPickTellers) {
        return
      }
      const participant = participants.find((p) => p.personaId === personaId)
      if (!participant) {
        return
      }
      const state = states[personaId]
      const next = nextCircleState(state, participant.enrichmentStatus === 'ready')
      // Off -> In and In -> Off (a twin that is not ready) are saves only: a viewer who
      // cannot save leaves the circle as it is.
      if (!canUpdateSettings && state !== 'teller' && next !== 'teller') {
        return
      }
      const active = new Set(activePersonaIds)
      if (next === 'bidder') {
        active.add(personaId)
        saveActive(active)
        return
      }
      if (next === 'teller') {
        updatePicks((prev) => ({ ...prev, sources: { ...prev.sources, [personaId]: 'circle' } }))
        return
      }
      // -> off
      if (pickSources[personaId] === 'tag') {
        unlinkNonceRef.current += 1
        setMentionUnlinkRequest({ participantId: participant.id, nonce: unlinkNonceRef.current })
      }
      updatePicks((prev) => ({
        sources: withoutKeys(prev.sources, [personaId]),
        pending: withoutKeys(prev.pending, [personaId]),
        restoredFrom: withoutKeys(prev.restoredFrom, [personaId]),
      }))
      // Without the save, a dropped pick falls back to the twin's saved state (In stays In).
      if (canUpdateSettings && active.has(personaId)) {
        active.delete(personaId)
        saveActive(active)
      }
    },
    [canPickTellers, canUpdateSettings, participants, states, activePersonaIds, pickSources, saveActive, updatePicks, setMentionUnlinkRequest],
  )

  const setTellerCount = useCallback(
    (value: number) => {
      if (tellerCount.isDisabled) {
        return
      }
      const clamped = Math.min(tellerCount.max, Math.max(tellerCount.min, value))
      if (clamped === tellerCount.value) {
        return
      }
      void onSettingsChange?.({ tellerCount: clamped })
    },
    [tellerCount, onSettingsChange],
  )

  const onMentionsChange = useCallback(
    (participantIds: Array<string>) => {
      const tagged = new Set(
        participants.filter((p) => participantIds.includes(p.id)).map((p) => p.personaId),
      )
      updatePicks((prev) => {
        const sources: Record<string, PickSource> = {}
        for (const [personaId, source] of Object.entries(prev.sources)) {
          if (source !== 'tag' || tagged.has(personaId)) {
            sources[personaId] = source
          }
        }
        for (const personaId of tagged) {
          if (!sources[personaId]) {
            sources[personaId] = 'tag'
          }
        }
        return { ...prev, sources }
      })
    },
    [participants, updatePicks],
  )

  const takeSendPicks = useCallback(() => {
    // Move the takeable picks to "sent, pending": the ring stays, the next send no longer carries them.
    const taken = orderByRoom(participants, new Set(Object.keys(readPicks().sources)))
    if (taken.length === 0) {
      lastTakeKeyRef.current = null
      return []
    }
    takeNonceRef.current += 1
    const takeKey = `take-${takeNonceRef.current}`
    lastTakeKeyRef.current = { roomToken, key: takeKey }
    updatePicks((prev) => {
      const pending = { ...prev.pending }
      for (const personaId of taken) {
        pending[personaId] = takeKey
      }
      return {
        sources: withoutKeys(prev.sources, taken),
        pending,
        restoredFrom: withoutKeys(prev.restoredFrom, taken),
      }
    })
    return taken
  }, [participants, readPicks, roomToken, updatePicks])

  const markSent = useCallback(
    (clientRequestId: string | undefined, sent: Array<string>) => {
      const take = lastTakeKeyRef.current
      lastTakeKeyRef.current = null
      const takeKey = take && take.roomToken === roomToken ? take.key : null
      if (!takeKey || sent.length === 0) {
        return
      }
      if (!clientRequestId) {
        // Refused before queueing (trial cap): nothing carries them, give them back.
        updatePicks((prev) => applySendOutcome(
          prev,
          { clientRequestId: takeKey, outcome: 'failed' },
        ))
        return
      }
      updatePicks((prev) => {
        const pending = { ...prev.pending }
        for (const personaId of sent) {
          if (pending[personaId] === takeKey) {
            pending[personaId] = clientRequestId
          }
        }
        return { ...prev, pending }
      })
    },
    [roomToken, updatePicks],
  )

  return {
    states,
    picks,
    bidderCount,
    isAllOff: bidderCount === 0,
    canUpdateSettings,
    canPickTellers,
    tellerCount,
    cycle,
    setTellerCount,
    onMentionsChange,
    mentionUnlinkRequest,
    takeSendPicks,
    markSent,
  }
}
